import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleBondInformationDto, VehicleDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatCurrency } from 'utils'
import BondInformationAddEditDialog from './BondInformationAddEditDialog'

interface BondInformationListCardProps {
  vehicle: VehicleDto
}

export default function BondInformationListCard({ vehicle }: BondInformationListCardProps) {
  const [BondInformationAddEditDialogOpen, setBondInformationAddEditDialogOpen] = useState<boolean>(false)
  const [isSavingBondInformation, setIsSavingBondInformation] = useState<boolean>(false)
  const [isGettingBondInformation, setIsGettingBondInformation] = useState<boolean>(false)
  const [bondInformation, setBondInformation] = useState<VehicleBondInformationDto>(new VehicleBondInformationDto())

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingBondInformation(true)
      controllers
        .getBondInformation(vehicle.vehicleGuid!)
        .then(response => {
          setBondInformation(response.value)
        })
        .finally(() => {
          setIsGettingBondInformation(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <BondInformationAddEditDialog
        disabled={Boolean(vehicle.vehicleGuid && vehicle.isLocked)}
        initialValues={bondInformation}
        isLoading={isSavingBondInformation}
        onClose={() => {
          setBondInformationAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingBondInformation(true)
          controllers
            .updateBondInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              setBondInformation(response.value)
              setBondInformationAddEditDialogOpen(false)
              enqueueSnackbar('Bond Information Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setIsSavingBondInformation(false)
            })
        }}
        open={BondInformationAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Bond Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setBondInformationAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingBondInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingBondInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Bond #' value={bondInformation.bondNumber} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Special Bond #' value={bondInformation.specialBondNumber} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Amount (USD)'
                    value={bondInformation.amountUSD ? formatCurrency(bondInformation.amountUSD) : ''}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Bond Threshold'
                    value={bondInformation.bondThreshold ? formatCurrency(bondInformation.bondThreshold) : ''}
                  />
                </Grid>

                {(bondInformation.amountUSD ?? 0) >= (bondInformation.bondThreshold ?? 0) && (
                  <Grid item xs={12}>
                    <Typography variant='body2' color='error'>
                      Vehicle Amount exceeds Bond Threshold.
                    </Typography>

                    <Typography variant='body2' color='error'>
                      Special Bond # is required.
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <LabelField
                    label='Bond Amount 150%'
                    value={bondInformation.amountUSD150Percent ? formatCurrency(bondInformation.amountUSD150Percent) : ''}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ mt: 2 }}>
              <ExplanationAccordion variant='outlined'>
                Bond# is a system-driven value that is not entered by a user. If the vehicle is locked and if the Amount (USD)
                is less than the Bond Threshold amount set under Settings, this field will be populated with the next available
                Bond Number from the current Bond Block. If the vehicle is not locked, the Bond# field will be blank. || Special
                Bond # shows whatever was entered into the Special Bond # input field in the “Add Bond Information” dialog. If a
                Special Bond # has not been entered, the Special Bond # field will be blank. || The Amount (USD) label shows the
                Amount (USD) value from the Add/Edit Vehicle Dialog. || The Bond Threshold label shows the Bond Threshold value
                from the Bond Policy card under Settings. || If the vehicle amount is greater than or equal to the Bond
                Threshold, there will be bold red text saying, “Vehicle Amount exceeds Bond Threshold. Special Bond # is
                required”. || Bond Amount 150% = Amount (USD) * 1.5 || Bond Amount 150% is displayed as both a numerical dollar
                amount and a written dollar amount like a check (i.e., displayed as “$25,500.69” and “Twenty-Five Thousand Five
                Hundred Dollars and 69/100”). Both formats are needed to support the Bond document generated when the vehicle is
                added to a PAPS and that PAPS is locked.
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
